import React, { useEffect, useState } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ImCross } from "react-icons/im";
import { FaEdit, FaTrash, FaChevronUp, FaChevronDown } from "react-icons/fa";
import Swal from "sweetalert2";
import useReminders from "../hooks/useReminders";
import { handleModalAction } from "../../../../utils/helpers/handleModalAction";
import EditReminderModal from "./EditReminderModal";
import { FiClock } from "react-icons/fi";
import { MdOutlineDescription } from "react-icons/md";
import { Link } from "react-router-dom";

const AllReminders = ({ onClose }) => {
  const [activeAccordion, setActiveAccordion] = useState("lead");
  const [filter, setFilter] = useState("all");
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [selectedModule, setSelectedModule] = useState("lead");

  const { deleteReminder, reminders: allReminders } = useReminders();

  const remindersByModule = {
    lead: allReminders?.filter((reminder) => reminder.module === "lead") || [],
    deal: allReminders?.filter((reminder) => reminder.module === "deal") || [],
  };

  const [filteredReminders, setFilteredReminders] = useState(
    remindersByModule.lead
  );

  useEffect(() => {
    setFilteredReminders(
      remindersByModule[activeAccordion]?.sort(
        (a, b) => new Date(b.reminderDateTime) - new Date(a.reminderDateTime)
      )
    );
  }, [activeAccordion, allReminders]);

  const filterReminders = (type, event) => {
    event.stopPropagation();
    setFilter(type);

    const remindersToFilter = remindersByModule[activeAccordion];
    const now = new Date();

    let filtered = [];

    if (type === "overdue") {
      filtered = remindersToFilter.filter(
        (r) => new Date(r.reminderDateTime) < now
      );
    } else if (type === "today") {
      filtered = remindersToFilter.filter((r) => {
        const reminderDate = new Date(r.reminderDateTime);
        return reminderDate.toDateString() === now.toDateString();
      });
    } else if (type === "upcoming") {
      filtered = remindersToFilter.filter(
        (r) => new Date(r.reminderDateTime) > now
      );
    } else {
      filtered = remindersToFilter;
    }

    // Sort in ascending order
    setFilteredReminders(
      filtered.sort(
        (a, b) => new Date(b.reminderDateTime) - new Date(a.reminderDateTime)
      )
    );
  };

  const handleEdit = async (reminder) => {
    setSelectedReminder(reminder);
    setSelectedModule(reminder.module);
    handleModalAction("editReminderModal", "show");
  };

  const handleDelete = async (reminderId) => {
    const result = await Swal.fire({
      title: "Delete Reminder?",
      text: "Are you sure you want to delete this reminder? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "small-swal", // Custom class name
      },
    });

    if (result.isConfirmed) {
      deleteReminder.mutate({ reminderId });
    }
  };

  const handleAccordionToggle = (module) => {
    setActiveAccordion((prev) => (prev === module ? "" : module));
  };

  const handleClose = () => {
    document.querySelector(".dropdown-menu.show")?.classList.remove("show");
  };

  return (
    <div
      className="dropdown-menu menu-drop-user shadow-lg p-3"
      style={{
        width: "350px",
        // maxHeight: "88vh",
        // overflowY: "auto"
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
        <h5 className="mb-0">Reminders</h5>
        <Button variant="light" size="sm" onClick={handleClose}>
          <ImCross />
        </Button>
      </div>

      {/* Accordion */}
      {Object?.keys(remindersByModule)?.map((module) => (
        <div key={module} className="mb-2">
          <div
            className={`accordion-header d-flex justify-content-between align-items-center ${
              activeAccordion === module ? "" : "collapsed"
            }`}
            onClick={() => handleAccordionToggle(module)}
            aria-expanded={activeAccordion === module}
            style={{
              cursor: "pointer",
              backgroundColor: "#f8f9fa",
              padding: "8px",
              borderRadius: "5px",
              fontWeight: "bold",
            }}
          >
            {module === "lead" ? "Lead Reminders" : "Deal Reminders"}
            {activeAccordion === module ? (
              <FaChevronUp size={16} />
            ) : (
              <FaChevronDown size={16} />
            )}
          </div>
          <div
            className={`accordion-collapse ${
              activeAccordion === module ? "show" : "collapse"
            }`}
          >
            <div className="accordion-body">
              {/* Filter Buttons */}
              <div className="d-flex justify-content-between mb-2">
                <Button
                  size="sm"
                  variant={filter === "overdue" ? "danger" : "outline-danger"}
                  onClick={(e) => filterReminders("overdue", e)}
                >
                  Overdue
                </Button>
                <Button
                  size="sm"
                  variant={filter === "today" ? "primary" : "outline-primary"}
                  onClick={(e) => filterReminders("today", e)}
                >
                  Today
                </Button>
                <Button
                  size="sm"
                  variant={filter === "upcoming" ? "info" : "outline-info"}
                  onClick={(e) => filterReminders("upcoming", e)}
                >
                  Upcoming
                </Button>
              </div>

              {/* Reminders List */}
              <ul className="list-group rounded-0 border-0 reminders-list">
                {filteredReminders?.length > 0 ? (
                  filteredReminders.map((reminder) => {
                    const reminderDate = new Date(reminder.reminderDateTime);
                    const now = new Date();

                    let bgColor = "bg-info-subtle";
                    let borderColor = "border-info";
                    let textColor = "text-dark";

                    if (reminderDate < now) {
                      bgColor = "bg-danger-subtle";
                      borderColor = "border-danger";
                      textColor = "text-danger";
                    } else if (reminderDate > now) {
                      bgColor = "bg-info-subtle";
                      borderColor = "border-info";
                      textColor = "text-info";
                    }

                    return (
                      <li
                        key={reminder._id}
                        className={`reminder-item list-group-item bg-opacity-10 ${bgColor} ${borderColor} p-2 mb-2`}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <h6>
                            <Link
                              className="text-capitalize fw-bold text-decoration-underline"
                              to={`/crm/${reminder.module}s/${reminder.leadId}?shouldRefetch=true`}
                              // state={{ forceReload: true }}
                            >
                              {reminder.leadMetaDetails?.leadName || "No Name"}
                            </Link>
                          </h6>
                          <span className="action-buttons">
                            <OverlayTrigger
                              overlay={
                                <Tooltip id="collapse-tooltip">Edit</Tooltip>
                              }
                            >
                              <FaEdit
                                style={{
                                  cursor: "pointer",
                                  marginRight: "8px",
                                  color: "green",
                                }}
                                onClick={() => handleEdit(reminder)}
                              />
                            </OverlayTrigger>
                            <OverlayTrigger
                              overlay={
                                <Tooltip id="collapse-tooltip">Delete</Tooltip>
                              }
                            >
                              <FaTrash
                                style={{ cursor: "pointer", color: "red" }}
                                onClick={() => handleDelete(reminder._id)}
                              />
                            </OverlayTrigger>
                          </span>
                        </div>
                        <small className="text-muted d-flex align-items-center gap-1">
                          <span>
                            <FiClock size={15} />
                          </span>
                          <span className="reminder-dateTime">
                            {reminderDate.toLocaleDateString("en-GB")}{" "}
                            {reminderDate.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}
                          </span>
                        </small>

                        <div
                          className={`text-muted d-flex align-items-center gap-1`}
                        >
                          <span>
                            <MdOutlineDescription size={15} />
                          </span>
                          <span className="reminder-desc text-wrap">
                            {reminder.reminderMessage}
                          </span>
                        </div>
                      </li>
                    );
                  })
                ) : (
                  <li className="list-group-item text-center text-muted">
                    No reminders found
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      ))}

      <EditReminderModal data={selectedReminder} module={selectedModule} />
    </div>
  );
};

export default AllReminders;
